import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Col, Overlay, Tooltip } from "react-bootstrap";
import { useDrag, useDrop } from 'react-dnd'
import ObjectId from 'bson-objectid';

import Icon from "../../../controls/Icon.jsx";
import Forms from "../../../../services/forms.js";
import PubSub from "../../../../services/PubSub.js";
import ConfirmationPrompt from "../../../controls/ConfirmationPrompt.jsx";
import EditItem from "./modals/EditItem.jsx";
import { useFormsConfig } from "../Context.js";
import ListStatus from "../controls/ListStatus.jsx";
import FORMS_DND_ITEMS from "../../../../config/forms-dnd.js";
import { copyItem } from "../utils.js";
import { useSharedSection } from "../SharedSectionContext.js";

const SectionItems = ({
    sectionId,
    forceMaxWidth,
    isShared = false,
    isReadOnly,
    refreshTemplate,
    deletePermissions
}) => {
    const { id: deploymentId } = useParams();
    const [translate] = useTranslation();
    const { formTemplate, isItemUsedInFormConditions, logAmplitudeEvent } = useFormsConfig();
    const { sharedSection } = useSharedSection();

    const section = sharedSection || formTemplate.sections.find(({ _id }) => _id === sectionId);
    isReadOnly = !!section.sharedSectionId || isReadOnly;

    if (!section) {
        if (formTemplate) {
            throw new Error(`Section (${sectionId}) not found in template (${formTemplate._id}). Please contact your administator if this problem persists.`);
        }
        else {
            throw new Error(`Section (${sectionId}) not found. Please contact your administator if this problem persists.`);
        }
    }

    const [items, setItems] = useState([]);
    const [copiedItem, setCopiedItem] = useState({});
    const [showAddItem, setShowAddItem] = useState(false);
    const [showEditItem, setShowEditItem] = useState(false);
    const [showDeleteItem, setShowDeleteItem] = useState(false);

    const resetItems = useCallback(() => {
        setItems(section.items.map((item) => item) || []);
    }, [section.items]);

    useEffect(() => {
        resetItems();
    }, [resetItems]);

    const renderAddItemButton = useMemo(() => {
        if (!isReadOnly) {
            return (
                <div className="d-flex justify-content-end mt-3">
                    <Button
                        id="btn_add_item"
                        size="sm"
                        variant="primary"
                        onClick={() => {
                            setShowAddItem(true);
                        }}
                    >
                        {translate("OTM_ADD_ITEM")}
                    </Button>
                </div>
            );
        }

        return null;
    }, [isReadOnly, translate, setShowAddItem]);

    const onReorderHover = useCallback(async (dragIndex, hoverIndex) => {
        const reorderingItems = items.map(({ _id }) => _id);
        const draggingItemId = reorderingItems[dragIndex];
        reorderingItems.splice(dragIndex, 1);
        reorderingItems.splice(hoverIndex, 0, draggingItemId);

        setItems(reorderingItems.map((itemId) => items.find(({ _id }) => _id === itemId)));
    }, [items]);

    const onReorderDrop = useCallback(async () => {
        if (isShared) {
            await Forms.reorderSharedSectionItems({
                deploymentId,
                sharedSectionId: sectionId,
                body: items.map(({ _id }) => _id)
            });
        }
        else {
            await Forms.reorderItems({
                deploymentId,
                formTemplateId: formTemplate._id,
                sectionId,
                body: items.map(({ _id }) => _id)
            });
        }
        await refreshTemplate();
    }, [deploymentId, formTemplate?._id, isShared, sectionId, items, refreshTemplate]);

    const onAddHover = useCallback(async (item, index) => {
        setItems((currentItems) => ([...currentItems.slice(0, index), item, ...currentItems.slice(index)]));
    }, []);

    const onAddDrop = useCallback(async (droppedItem) => {
        const idReplacement = {
            [droppedItem._storage.sectionId]: sectionId
        };
        let newOrder;
        if (!items || items.length === 0) {
            newOrder = 0;
        }
        else {
            const foundIndex = items.findIndex(item => item._id === droppedItem._id);
            newOrder = foundIndex >= 1 ? items[foundIndex - 1].order : items[items.length - 1].order
        }
        droppedItem.order = newOrder;
        setCopiedItem(copyItem(droppedItem, idReplacement));
        setShowAddItem(true);
    }, [sectionId, items]);

    const handleDeleteItem = useCallback(async () => {
        const itemId = showDeleteItem;
        const result = isItemUsedInFormConditions({ itemId });

        if (result.isUsed) {
            PubSub.publish("show_info_item_usage", {
                whereUsed: result.whereUsed,
                title: translate("OTM_CANNOT_DELETE_ITEM"),
                initialLabel: translate("OTM_DELETE_ITEM_CHECK_INITIAL_TEXT")
            });
        }
        else {
            if (isShared) {
                await Forms.deleteSharedSectionItem({
                    deploymentId,
                    sharedSectionId: section._id,
                    sharedSectionItemId: itemId,
                });
            }
            else {
                await Forms.deleteItem({
                    deploymentId,
                    formTemplateId: formTemplate._id,
                    sectionId: section._id,
                    itemId,
                });
            }
            logAmplitudeEvent({
                eventType: `deleted${isShared ? 'Shared' : ''}Item`,
                eventProperties: {
                    deploymentId,
                    formTemplateId: formTemplate?._id,
                    formTemplateName: formTemplate?.name,
                    sectionName: section.name,
                    itemName: section.items?.find((item) => item._id === itemId)?.name
                }
            });
            await refreshTemplate();
        }
        setShowDeleteItem(false);
    }, [showDeleteItem, deploymentId, formTemplate, section._id, section.name, section.items, isShared, logAmplitudeEvent, isItemUsedInFormConditions, refreshTemplate, translate]);

    const addItemModal = useMemo(() => showAddItem ? (
        <EditItem
            formTemplateId={formTemplate?._id}
            section={section}
            isEdit={false}
            isShared={isShared}
            defaultShowSave={!!copiedItem.name}
            item={{
                _id: new ObjectId().toHexString(),
                ...copiedItem
            }}
            show={showAddItem}
            allowUpdate={!isReadOnly}
            onAdd={async () => {
                await refreshTemplate();
                setCopiedItem({});
                setShowAddItem(false);
            }}
            onClose={() => {
                resetItems();
                setCopiedItem({});
                setShowAddItem(false);
            }}
        />
    ) : null, [formTemplate?._id, section, isShared, copiedItem, resetItems, showAddItem, isReadOnly, refreshTemplate]);

    const editItemModal = useMemo(() => showEditItem ? (
        <EditItem
            formTemplateId={formTemplate?._id}
            isEdit={true}
            isShared={isShared}
            item={showEditItem}
            section={section}
            show={showEditItem}
            allowUpdate={!isReadOnly}
            onUpdate={async () => {
                await refreshTemplate();
                setShowEditItem(false);
            }}
            onClose={() => {
                setShowEditItem(false);
            }}
        />
    ) : null, [formTemplate?._id, section, isShared, showEditItem, isReadOnly, refreshTemplate]);

    const deleteItemModal = useMemo(() => (
        <ConfirmationPrompt
            show={!!showDeleteItem}
            message={translate("OTM_CONFIRM_DELETE_ITEM")}
            onConfirm={handleDeleteItem}
            onCancel={() => {
                setShowDeleteItem(false);
            }}
        />
    ), [showDeleteItem, handleDeleteItem, translate]);

    return (
        <>
            {items.length === 0 ?
                <EmptyItemsList
                    renderAddItemButton={renderAddItemButton}
                    onAddDrop={onAddDrop}
                    isReadOnly={isReadOnly}
                /> :
                <div id="items_list" className="flex-lg-grow-1 overflow-x-hidden overflow-y-lg-auto padded-scrollbar">
                    <div className="row mt-n3">
                        {items.map((item, index) => <ListItem
                            item={item}
                            forceMaxWidth={forceMaxWidth}
                            sectionId={sectionId}
                            isShared={isShared}
                            isReadOnly={isReadOnly}
                            deletePermissions={deletePermissions}
                            setCopiedItem={setCopiedItem}
                            setShowAddItem={setShowAddItem}
                            setShowEditItem={setShowEditItem}
                            setShowDeleteItem={setShowDeleteItem}
                            onReorderHover={onReorderHover}
                            onReorderDrop={onReorderDrop}
                            onReorderDropOutside={() => {
                                resetItems();
                            }}
                            onAddHover={onAddHover}
                            onAddDrop={onAddDrop}
                            onAddDropOutside={() => {
                                resetItems();
                            }}
                            index={index}
                            key={item._id}
                        />)}
                    </div>
                    {renderAddItemButton}
                </div>
            }
            {addItemModal}
            {editItemModal}
            {deleteItemModal}
        </>
    );
};

const EmptyItemsList = ({
    renderAddItemButton,
    onAddDrop,
    isReadOnly
}) => {
    const [translate] = useTranslation();

    const [{ canDrop }, drop] = useDrop({
        accept: FORMS_DND_ITEMS.ITEM_ANY,
        collect: (monitor) => ({
            canDrop: monitor.canDrop()
        }),
        drop: async (droppedItem) => {
            await onAddDrop(droppedItem);

            return { location: 'items' };
        }
    }, [onAddDrop]);

    if (!isReadOnly && canDrop) {
        return (
            <div ref={drop} className={`card btn btn-sm btn-dropzone-forms droppable w-100`}>
                <div className="card-body">
                    <span>{translate('OTM_FORMS_ITEM_DROP')}</span>
                </div>
            </div>
        )
    }
    return (
        <>
            <ListStatus loading={false} error={false} empty={true} emptyText={translate("OTM_NO_ITEMS")} ></ListStatus>
            <div className="overflow-x-hidden">
                {renderAddItemButton}
            </div>
        </>
    );
};

const ListItem = ({
    item,
    forceMaxWidth = false,
    sectionId,
    isShared,
    isReadOnly,
    deletePermissions,
    setCopiedItem,
    setShowAddItem,
    setShowEditItem,
    setShowDeleteItem,
    onReorderHover,
    onReorderDrop,
    onReorderDropOutside,
    onAddHover,
    onAddDrop,
    onAddDropOutside,
    index
}) => {
    const ref = useRef(null);
    const [translate] = useTranslation();
    const [showTooltip, setShowTooltip] = useState(false);
    const [scrolledIntoView, setScrolledIntoView] = useState(false);
    const [{ isDragging }, drag] = useDrag(() => ({
        type: isShared ? FORMS_DND_ITEMS.SHARED_SECTION_ITEM : FORMS_DND_ITEMS.ITEM,
        item: {
            ...item,
            _storage: {
                _id: item._id,
                sectionId
            },
            _dragging: {
                index
            }
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        }),
        end: async (droppedItem, monitor) => {
            setScrolledIntoView(false);
            const didDrop = monitor.didDrop()
            const { location } = monitor.getDropResult() || {};
            if (!didDrop || location === 'clipboard') {
                if (monitor.getItemType() !== FORMS_DND_ITEMS.ITEM_COPIED) {
                    await onReorderDropOutside();
                }
                else {
                    await onAddDropOutside();
                }
            }

            droppedItem._dragging.index = index;
        },
    }), [item, index, onReorderDropOutside, onAddDropOutside]);
    const [{ dropType, canDrop }, drop] = useDrop({
        accept: isReadOnly ? [] : FORMS_DND_ITEMS.ITEM_ANY,
        hover: async (item, monitor) => {
            if (isReadOnly) {
                return;
            }

            if (item._dragging.index === undefined) {
                item._dragging.index = index;
                item._dragging.isAdding = true;
                await onAddHover(item, index);
                item._dragging.onDropOutside = onAddDropOutside;

                return;
            }

            const dragIndex = item._dragging.index;
            const hoverIndex = index;

            if (ref.current && dragIndex !== hoverIndex) {
                const hoverBoundingRect = ref.current.getBoundingClientRect();
                const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
                const clientOffset = monitor.getClientOffset();
                const hoverClientY = clientOffset.y - hoverBoundingRect.top;

                const isDraggingDown = dragIndex < hoverIndex && hoverClientY > hoverMiddleY;
                const isDraggingUp = dragIndex > hoverIndex && hoverClientY < hoverMiddleY;

                if (isDraggingDown || isDraggingUp) {
                    item._dragging.index = hoverIndex;
                    await onReorderHover(dragIndex, hoverIndex);
                }
            }
        },
        collect: (monitor) => ({
            canDrop: !isReadOnly && monitor.canDrop(),
            dropType: monitor.getItemType()
        }),
        drop: async (droppedItem, monitor) => {
            if (monitor.getItemType() !== FORMS_DND_ITEMS.ITEM_COPIED) {
                await onReorderDrop();
            }
            else {
                await onAddDrop(droppedItem);
            }

            return { location: 'items' };
        }
    }, [onReorderHover, onReorderDrop, onAddHover, onAddDrop, index, isReadOnly]);

    drag(drop(ref));

    useEffect(() => {
        if (isDragging && scrolledIntoView === false) {
            setScrolledIntoView(true);
            ref.current.scrollIntoView({ behaviour: 'smooth', block: 'nearest' });
        }
    }, [isDragging, scrolledIntoView]);

    const icon = useMemo(() => {
        let iconName = 'DragIndicatorOutlined';
        if (!isReadOnly) {
            if (item.order < index + 1) {
                iconName = 'ArrowDown';
            }
            else if (item.order > index + 1) {
                iconName = 'ArrowUp';
            }
            else if (dropType === FORMS_DND_ITEMS.ITEM) {
                iconName = 'Height';
            }
        }

        return (
            <Icon icon={iconName} className="text-tertiary" />
        );
    }, [dropType, item.order, index, isReadOnly]);

    const [spanlarge, setSpanLarge] = useState('12');

    // react dnd/Chrome issue - can't drag if initial render doesn't have width, so drag does not work if span doesn't start at 12
    useEffect(() => {
        if (!canDrop) {
            setSpanLarge(forceMaxWidth ? '12' : item.columnSpanLarge);
        }
        else {
            setSpanLarge('12');
        }
    }, [canDrop, item, forceMaxWidth]);

    const renderConditionalIcons = useMemo(() =>
        <>
            {item.visible && item.visible.length > 0 && <Icon icon="Visibility" className="text-primary d-block fs-5 ms-1" />}
            {((item.readOnly && item.readOnly.length > 0) || item.isReadOnly) && <Icon icon="Lock" className="text-accent d-block fs-5 ms-1" />}
            {item.validation && item.validation.length > 0 && <Icon icon="CheckCircleOutline" className="text-success d-block fs-5 ms-1" />}
            {item.helpText && <Icon icon="QuestionCircle" className="text-info d-block fs-5 ms-1" />}
        </>, [item]);

    const renderTooltip = useMemo(() =>
        <Overlay
            show={showTooltip}
            target={ref.current}
            placement="top" >
            <Tooltip
                style={{ '--bs-tooltip-max-width': 'none' }}
                id={index}>
                <div className="d-flex flex-column align-items-start">
                    <div className="d-flex">
                        <span>
                            {item.name}
                        </span>
                        {item.isRequired === true ?
                            <span className="ms-1">{translate("OTM_ITEM_IS_REQUIRED")}</span> :
                            <span className="ms-1">{translate("OTM_ITEM_NOT_REQUIRED")}</span>
                        }
                    </div>
                    {item.dataType === 'select-option' && <>
                        <div>
                            <span>{translate("OTM_DATA_LIST")}: {item.dataList?.name}</span>
                        </div>
                        <div>
                            <span>{translate("OTM_ALLOW_MULTIPLE")}: {item.multipleOptions === true ? translate("OTM_YES") : translate("OTM_NO")}</span>
                        </div>
                        <div>
                            <span>{translate("OTM_DROPDOWN_ALLOW_SEARCH")}: {item.searchableDropdown === true ? translate("OTM_YES") : translate("OTM_NO")}</span>
                        </div>
                    </>}
                    <div>
                        <span>{translate("OTM_TOOLTIP_HIGHLIGHT_INVALID", { value: item.showStylesWhenInvalid === true ? translate("OTM_YES") : translate("OTM_NO") })} </span>
                    </div>
                    <div>
                        <span>{translate("OTM_TOOLTIP_HIGHLIGHT_VALID", { value: item.showStylesWhenValid === true ? translate("OTM_YES") : translate("OTM_NO") })}</span>
                    </div>
                </div>
            </Tooltip>
        </Overlay>, [index, item, showTooltip, translate]);

    return (
        <>
            {renderTooltip}
            <Col
                ref={ref}
                sm={12}
                lg={spanlarge}
                className={`mt-3 moveable`}
                onMouseEnter={() => { if (!canDrop) { setShowTooltip(true) } }}
                onMouseLeave={() => { setShowTooltip(false) }}>
                <div className={`card border h-100 ${isDragging || item._dragging?.isAdding ? 'forms-item-dragging' : ''}`}>
                    <div id={`item_${item.name}`} className="card-body d-flex flex-column">
                        <div className="row flex-grow-1 mt-n2 align-items-center justify-content-end">
                            {canDrop && <div className="col-auto mt-2 p-0 order-first">
                                {item.order === index + 1 ? null : <span className="text-decoration-line-through">{item.order}</span>}
                                {<span className={`ms-1 ${item.order === index + 1 ? '' : 'text-tertiary'}`}>{index + 1}</span>}
                            </div>}
                            <div className="col-auto mt-2 p-0 order-last order-lg-first">
                                {icon}
                            </div>
                            <div className="col-12 col-lg mt-2">
                                <label className={`d-block h6 mb-0 ${item.labelBolded ? 'fw-bold' : ''} ${item.labelItalic ? 'fst-italic' : ''} ${item.labelUnderline ? 'text-decoration-underline' : ''}`}>
                                    {`${item.labelValue || item.name}`}
                                </label>
                                <div className="d-flex align-items-center">
                                    {item.dataType ?
                                        <label className="d-block small mb-0">
                                            {`${item.dataType}`}
                                            {item.isRequired === true && <span className="text-danger ms-1">*</span>}
                                        </label>
                                        : null}
                                    {renderConditionalIcons}
                                </div>

                            </div>
                            <div className="col-auto mt-2">
                                <div className="row gx-2 align-items-center justify-content-end">
                                    {!isReadOnly && (
                                        <div className="col-auto">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                className="p-0 border-0"
                                                onClick={() => {
                                                    const idReplacement = {
                                                        [sectionId]: sectionId
                                                    };
                                                    setCopiedItem(copyItem(item, idReplacement));
                                                    setShowAddItem(true);
                                                }}
                                                title={translate("OTM_FORMS_ITEM_COPY")}
                                            ><Icon icon="FileCopy" className="d-block" /></Button>
                                        </div>
                                    )}
                                    <div className="col-auto">
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            className="p-0 border-0"
                                            onClick={() => {
                                                setShowEditItem(item);
                                            }}
                                            title={translate("OTM_EDIT")}
                                        ><Icon icon="Settings" className="d-block" /></Button>
                                    </div>
                                    {!isReadOnly && deletePermissions && <div className="col-auto">
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            className="p-0 border-0"
                                            onClick={() => {
                                                setShowDeleteItem(item._id);
                                            }}
                                            title={translate("OTM_DELETE")}
                                        ><Icon icon="Delete" className="d-block" /></Button>
                                    </div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Col>
        </>
    );
};

export default SectionItems;